// Sidebar.js
import React from 'react';

const Sidebar = ({ user, activeTab, setActiveTab, isOpen, onToggle, onLogout }) => {
  // Define menu items based on user role
  const getMenuItems = () => {
    const commonItems = [];

    if (user.role === 'user') {
      return [
        { id: 'overview', label: 'Overview', icon: '📊' },
        { id: 'verify', label: 'Verify Certificate', icon: '🔍' },
        { id: 'history', label: 'Verification History', icon: '📋' },
        { id: 'profile', label: 'Profile Settings', icon: '👤' }
      ];
    }

    if (user.role === 'admin') {
      return [
        { id: 'overview', label: 'Dashboard Overview', icon: '📊' },
        { id: 'users', label: 'Manage Users', icon: '👥' },
        { id: 'upload', label: 'Upload Certificates', icon: '📤' },
        { id: 'certificates', label: 'View Certificates', icon: '📜' },
        { id: 'reports', label: 'Verification Reports', icon: '📈' },
        { id: 'settings', label: 'Verification Fee Settings', icon: '⚙️' },
        { id: 'logs', label: 'Activity Logs', icon: '📝' }
      ];
    }

    if (user.role === 'super_admin') {
      return [
        { id: 'overview', label: 'Dashboard Overview', icon: '📊' },
        { id: 'users', label: 'Manage Users', icon: '👥' },
        { id: 'admins', label: 'Manage Admins', icon: '🛡️' },
        { id: 'upload', label: 'Upload Certificates', icon: '📤' },
        { id: 'certificates', label: 'View Certificates', icon: '📜' },
        { id: 'reports', label: 'Verification Reports', icon: '📈' },
        { id: 'settings', label: 'Verification Fee Settings', icon: '⚙️' },
        { id: 'super-settings', label: 'Super Admin Settings', icon: '🔧' },
        { id: 'logs', label: 'Activity Logs', icon: '📝' }
      ];
    }

    return commonItems;
  };

  const menuItems = getMenuItems();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Logo Section */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="brand-logo">
              <div className="logo-container">
                <img
                  src="/images/logo.png"
                  alt="OOU Logo"
                  className="brand-logo-image"
                  onError={(e) => {
                    // Fallback to different image formats if the first one fails
                    if (e.target.src.includes('logo.png')) {
                      e.target.src = '/images/logo.jpg';
                    } else if (e.target.src.includes('logo.jpg')) {
                      e.target.src = '/images/logo.svg';
                    } else {
                      // If all image formats fail, show fallback text
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="logo-fallback" style={{ display: 'none' }}>
                  OU
                </div>
              </div>
              {isOpen && <span className="brand-text">OOU EPROFICIENCY</span>}
            </div>
            <button
              onClick={onToggle}
              className="mobile-close-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* User Info Section */}
        {isOpen && (
          <div className="sidebar-user-section">
            <div className="user-profile">
              <div className="user-avatar">
                {user.firstname.charAt(0)}{user.lastname.charAt(0)}
              </div>
              <div className="user-info">
                <div className="user-name">
                  {user.firstname} {user.lastname}
                </div>
                <div className="user-role">
                  {user.role.replace('_', ' ')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="sidebar-navigation">
          <ul className="nav-menu">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`nav-item ${activeTab === item.id ? 'nav-item-active' : ''}`}
                  title={!isOpen ? item.label : ''}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {isOpen && <span className="nav-label">{item.label}</span>}
                  {isOpen && activeTab === item.id && (
                    <div className="active-indicator"></div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer Actions */}
        <div className="sidebar-footer">
          <button
            onClick={onLogout}
            className="logout-btn"
            title={!isOpen ? 'Logout' : ''}
          >
            <span className="logout-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16,17 21,12 16,7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </span>
            {isOpen && <span className="logout-label">Sign Out</span>}
          </button>
        </div>
      </div>

      {/* Professional Sidebar Styles */}
      <style jsx>{`
                .sidebar-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.6);
                    backdrop-filter: blur(4px);
                    z-index: 40;
                    display: none;
                }

                @media (max-width: 768px) {
                    .sidebar-overlay {
                        display: block;
                    }
                }

                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    height: 100vh;
                    background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 50;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.04);
                    border-right: 1px solid rgba(148, 163, 184, 0.1);
                }

                .sidebar-open {
                    width: 280px;
                }

                .sidebar-closed {
                    width: 80px;
                }

                .sidebar-header {
                    flex-shrink: 0;
                    padding: 1.5rem 1rem;
                    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
                }

                .sidebar-brand {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .brand-logo {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .logo-container {
                    width: 40px;
                    height: 40px;
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                }

                .brand-logo-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center;
                    border-radius: 12px;
                }

                .logo-fallback {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 700;
                    font-size: 0.875rem;
                    letter-spacing: 0.5px;
                }

                .brand-text {
                    color: #f8fafc;
                    font-weight: 600;
                    font-size: 1.125rem;
                    letter-spacing: -0.025em;
                }

                .mobile-close-btn {
                    display: none;
                    padding: 0.5rem;
                    color: #cbd5e1;
                    background: none;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .mobile-close-btn:hover {
                    background: rgba(148, 163, 184, 0.1);
                    color: #f8fafc;
                }

                @media (max-width: 768px) {
                    .mobile-close-btn {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                }

                .sidebar-user-section {
                    flex-shrink: 0;
                    padding: 1.5rem;
                    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
                }

                .user-profile {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .user-avatar {
                    width: 44px;
                    height: 44px;
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    font-size: 0.875rem;
                    letter-spacing: 0.5px;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.3);
                }

                .user-info {
                    flex: 1;
                    min-width: 0;
                }

                .user-name {
                    color: #f8fafc;
                    font-weight: 600;
                    font-size: 0.875rem;
                    line-height: 1.25;
                    margin-bottom: 0.125rem;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .user-role {
                    color: #94a3b8;
                    font-size: 0.75rem;
                    text-transform: capitalize;
                    font-weight: 500;
                    letter-spacing: 0.025em;
                }

                .sidebar-navigation {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1rem 0;
                }

                .nav-menu {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .nav-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: 0.75rem 1.5rem;
                    color: #cbd5e1;
                    background: none;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                    font-weight: 500;
                    border-radius: 0;
                    text-align: left;
                }

                .nav-item:hover {
                    background: rgba(148, 163, 184, 0.1);
                    color: #f8fafc;
                }

                .nav-item-active {
                    background: rgba(59, 130, 246, 0.1);
                    color: #60a5fa;
                    border-right: 3px solid #3b82f6;
                }

                .nav-icon {
                    font-size: 1.25rem;
                    width: 1.5rem;
                    height: 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .nav-label {
                    margin-left: 0.75rem;
                    font-weight: 500;
                    white-space: nowrap;
                    letter-spacing: -0.025em;
                }

                .active-indicator {
                    position: absolute;
                    right: 1rem;
                    width: 6px;
                    height: 6px;
                    background: #3b82f6;
                    border-radius: 50%;
                    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
                }

                .sidebar-footer {
                    flex-shrink: 0;
                    padding: 1rem 0 1.5rem;
                    border-top: 1px solid rgba(148, 163, 184, 0.1);
                }

                .logout-btn {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: 0.75rem 1.5rem;
                    color: #cbd5e1;
                    background: none;
                    border: none;
                    border-radius: 0;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 0.875rem;
                    font-weight: 500;
                    text-align: left;
                }

                .logout-btn:hover {
                    background: rgba(239, 68, 68, 0.1);
                    color: #f87171;
                }

                .logout-icon {
                    width: 1.5rem;
                    height: 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .logout-label {
                    margin-left: 0.75rem;
                    font-weight: 500;
                    white-space: nowrap;
                    letter-spacing: -0.025em;
                }

                @media (max-width: 768px) {
                    .sidebar {
                        transform: translateX(-100%);
                    }
                    
                    .sidebar-open {
                        transform: translateX(0);
                        width: 320px;
                    }
                    
                    .sidebar-closed {
                        transform: translateX(-100%);
                    }
                }

                /* Custom scrollbar styling */
                .sidebar-navigation::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-navigation::-webkit-scrollbar-track {
                    background: rgba(148, 163, 184, 0.05);
                    border-radius: 3px;
                }

                .sidebar-navigation::-webkit-scrollbar-thumb {
                    background: rgba(148, 163, 184, 0.2);
                    border-radius: 3px;
                    transition: background 0.2s ease;
                }

                .sidebar-navigation::-webkit-scrollbar-thumb:hover {
                    background: rgba(148, 163, 184, 0.4);
                }

                /* Firefox scrollbar styling */
                .sidebar-navigation {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(148, 163, 184, 0.2) transparent;
                }

                /* Focus states for accessibility */
                .nav-item:focus,
                .logout-btn:focus,
                .mobile-close-btn:focus {
                    outline: 2px solid #3b82f6;
                    outline-offset: -2px;
                }

                /* Smooth transitions for responsive behavior */
                @media (prefers-reduced-motion: reduce) {
                    .sidebar,
                    .nav-item,
                    .logout-btn,
                    .mobile-close-btn {
                        transition: none;
                    }
                }
            `}</style>
    </>
  );
};

export default Sidebar;